import React, { useEffect, useState } from 'react';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { apiClient } from '../../api/client';
import { PaymentMethod, Goal } from '../../types';
import { formatCurrency } from '../../utils/format';
import { useAppStore } from '../../store/useAppStore';
import './PaymentMethodSelector.css';
import './CategorySelector.css'; // Используем общие стили для step-content

interface SourceSelection {
  type: 'account' | 'goal';
  id?: string;
  goalId?: string;
}

interface PaymentMethodSelectorProps {
  value?: string;
  sourceType?: 'account' | 'goal';
  sourceGoalId?: string;
  transactionType: 'income' | 'expense';
  onChange: (paymentMethodId: string | undefined, sourceType?: 'account' | 'goal', goalId?: string) => void;
  onBack: () => void;
}

export const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({ 
  value, 
  sourceType,
  sourceGoalId,
  transactionType,
  onChange, 
  onBack 
}) => {
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [selectedSource, setSelectedSource] = useState<SourceSelection>(() => {
    if (sourceType === 'goal' && sourceGoalId) {
      return { type: 'goal', goalId: sourceGoalId };
    }
    if (value) {
      return { type: 'account', id: value };
    }
    return { type: 'account' };
  });
  const [loading, setLoading] = useState(true);
  const { settings } = useAppStore();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [methodsData, goalsData] = await Promise.all([
        apiClient.getPaymentMethods(),
        // Загружаем цели только для расходов
        transactionType === 'expense' ? apiClient.getGoals() : Promise.resolve({ goals: [] })
      ]);
      
      // Сортируем: счет по умолчанию первым
      const methods = methodsData.payment_methods || [];
      const sortedMethods = [...methods].sort((a, b) => {
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;
        return 0;
      });
      
      setMethods(sortedMethods);
      
      // Автоматически выбираем счет по умолчанию, если он есть и счет не был выбран ранее
      if (!value && sortedMethods.length > 0) {
        const defaultMethod = sortedMethods.find(m => m.is_default);
        if (defaultMethod) {
          setSelectedSource({ type: 'account', id: defaultMethod.id });
          // Автоматически применяем выбор счет по умолчанию
          onChange(defaultMethod.id, 'account', undefined);
        }
      }
      
      // Фильтруем только активные цели с балансом > 0
      const activeGoals = (goalsData.goals || []).filter(
        (g: Goal) => g.status === 'ACTIVE' && g.current_amount > 0
      );
      setGoals(activeGoals);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAccount = (methodId: string) => {
    setSelectedSource({ type: 'account', id: methodId });
    // Сразу переходим на следующий шаг
    onChange(methodId, 'account', undefined);
  };

  const handleSelectGoal = (goalId: string) => {
    setSelectedSource({ type: 'goal', goalId });
    // Сразу переходим на следующий шаг
    onChange(undefined, 'goal', goalId);
  };

  const handleSkip = () => {
    onChange(undefined, undefined, undefined);
  };

  const hasItems = methods.length > 0 || (transactionType === 'expense' && goals.length > 0);

  return (
    <div className="step-content">
      <div className="step-content__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="step-content__back-btn neo-raised" onClick={onBack}>
            <Icon name="chevron_left" size={20} />
          </button>
          <div>
            <h2 className="step-content__title">
              {transactionType === 'expense' ? 'Откуда списать?' : 'Куда зачислить?'}
            </h2>
            <p className="step-content__subtitle">
              {transactionType === 'expense' 
                ? 'Выберите счет или цель для списания' 
                : 'Выберите счет для операции'}
            </p>
          </div>
        </div>
      </div>

      <div className="step-content__body">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#8E8E93' }}>
            Загрузка...
          </div>
        ) : !hasItems ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            background: 'transparent',
            border: '2px solid var(--color-divider)',
            borderRadius: '12px'
          }}>
            <Icon name="credit_card" size={48} color="var(--color-text-secondary)" />
            <p style={{ marginTop: '16px', color: 'var(--color-text-secondary)', fontSize: '14px' }}>
              У вас пока нет счетов{transactionType === 'expense' ? ' и целей с накоплениями' : ''}.
              <br />
              Добавьте их в настройках.
            </p>
          </div>
        ) : (
          <div className="payment-methods-list">
            {/* Счета */}
            {methods.length > 0 && (
              <>
                <div style={{ 
                  padding: '8px 12px', 
                  fontSize: '13px', 
                  fontWeight: '600', 
                  color: '#8E8E93',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Счета
                </div>
                {methods.map((method) => (
                  <button
                    key={`account-${method.id}`}
                    className={`payment-method-item neo-raised ${selectedSource.type === 'account' && selectedSource.id === method.id ? 'payment-method-item--selected' : ''}`}
                    onClick={() => handleSelectAccount(method.id)}
                  >
                    <div className="payment-method-item__icon">
                      <Icon name="credit_card" size={24} />
                    </div>
                    <div className="payment-method-item__info">
                      <div className="payment-method-item__name">
                        {method.name}
                        {method.is_default && (
                          <span style={{ marginLeft: '8px', fontSize: '12px', color: '#FF8C00', fontWeight: '600' }}>
                            (по умолчанию)
                          </span>
                        )}
                      </div>
                    </div>
                    {selectedSource.type === 'account' && selectedSource.id === method.id && (
                      <div className="payment-method-item__check">
                        <Icon name="check" size={20} color="#007AFF" />
                      </div>
                    )}
                  </button>
                ))}
              </>
            )}

            {/* Цели - только для расходов */}
            {transactionType === 'expense' && goals.length > 0 && (
              <>
                <div style={{ 
                  padding: '16px 12px 8px', 
                  fontSize: '13px', 
                  fontWeight: '600', 
                  color: '#8E8E93',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginTop: methods.length > 0 ? '8px' : '0'
                }}>
                  Цели
                </div>
                {goals.map((goal) => (
                  <button
                    key={`goal-${goal.id}`}
                    className={`payment-method-item neo-raised ${selectedSource.type === 'goal' && selectedSource.goalId === goal.id ? 'payment-method-item--selected' : ''}`}
                    onClick={() => handleSelectGoal(goal.id)}
                  >
                    <div className="payment-method-item__icon">
                      {goal.icon ? (
                        <span style={{ fontSize: '24px' }}>{goal.icon}</span>
                      ) : (
                        <Icon name="target" size={24} color="var(--color-primary)" />
                      )}
                    </div>
                    <div className="payment-method-item__info">
                      <div className="payment-method-item__name">{goal.name}</div>
                      <div style={{ fontSize: '13px', color: '#8E8E93', marginTop: '2px' }}>
                        Накоплено: {formatCurrency(goal.current_amount, settings?.currency || 'RUB')}
                      </div>
                    </div>
                    {selectedSource.type === 'goal' && selectedSource.goalId === goal.id && (
                      <div className="payment-method-item__check">
                        <Icon name="check" size={20} color="#007AFF" />
                      </div>
                    )}
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {hasItems && (
        <div className="step-content__footer">
          <Button 
            variant="secondary" 
            className="neo-raised"
            onClick={handleSkip} 
            fullWidth
          >
            Пропустить
          </Button>
        </div>
      )}
      
      {!hasItems && (
        <div className="step-content__footer">
          <Button 
            variant="primary" 
            className="neo-raised"
            onClick={handleSkip} 
            fullWidth
          >
            Продолжить без счета
          </Button>
        </div>
      )}
    </div>
  );
};

